import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  InputNumber,
  Popconfirm,
  Skeleton,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import type { MarkEntryRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Marks entry.
 * Route: /admin/exams/:code/marks?subject=<exam_subject_id>
 * If ?subject is missing, the page redirects back to the exam detail so the
 * user picks a subject there.
 */
export function MarksProgressPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const [sp] = useSearchParams();
  const subjectId = sp.get('subject');
  const navigate = useNavigate();

  useEffect(() => {
    if (!subjectId) navigate(`/i/${institution}/admin/exams/${code}`, { replace: true });
  }, [subjectId, institution, code, navigate]);

  if (!subjectId) return null;
  return <Sheet institution={institution} examId={code} examSubjectId={subjectId} />;
}

function Sheet({ institution, examId, examSubjectId }: { institution: string; examId: string; examSubjectId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState<Record<string, MarkEntryRow>>({});

  const q = useQuery({
    queryKey: ['marks-sheet', institution, examSubjectId],
    queryFn: () => productApi.getMarksSheet(institution, examSubjectId),
    enabled: !!institution && !!examSubjectId,
  });

  const save = useMutation({
    mutationFn: () => productApi.saveMarks(institution, examSubjectId, {
      exam_subject_id: examSubjectId,
      entries: Object.values(pending).map((e) => ({
        student_id: e.student_id,
        marks_obtained: e.absent ? null : e.marks_obtained,
        absent: e.absent,
        note: e.note ?? undefined,
      })),
    }),
    onSuccess: (r) => {
      setPending({});
      void qc.invalidateQueries({ queryKey: ['marks-sheet', institution, examSubjectId] });
      void qc.invalidateQueries({ queryKey: ['exam', institution, examId] });
      void message.success(`${r.updated} row${r.updated === 1 ? '' : 's'} saved`);
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Save failed'),
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Marks sheet not found" />;

  const es = q.data.exam_subject;
  const entries = q.data.entries;
  const merged = entries.map((e) => pending[e.student_id] ?? e);
  const enteredCount = merged.filter((e) => e.marks_obtained !== null || e.absent).length;
  const dirty = Object.keys(pending).length;

  const update = (student_id: string, patch: Partial<MarkEntryRow>) => {
    const row = pending[student_id] ?? entries.find((e) => e.student_id === student_id)!;
    setPending((p) => ({ ...p, [student_id]: { ...row, ...patch } }));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/exams/${examId}`)}>
          ← Exam
        </Button>
        <Typography.Text type="secondary">/</Typography.Text>
        <Typography.Text style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{es.subject_code} · {es.grade}{es.section ? `-${es.section}` : ''}</Typography.Text>
      </Space>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>{es.subject_name} · {es.grade}{es.section ? `-${es.section}` : ''}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Max {es.max_marks} · Passing {es.passing_marks} · Date {es.date}
          </Typography.Text>
        </div>
        <Space>
          <Button type="primary" icon={<SaveOutlined />} onClick={() => save.mutate()} disabled={dirty === 0} loading={save.isPending}>
            Save {dirty > 0 && `(${dirty})`}
          </Button>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Students" value={entries.length} /></Card>
        <Card size="small"><Statistic title="Entered" value={enteredCount} /></Card>
        <Card size="small"><Statistic title="Pending" value={entries.length - enteredCount} valueStyle={{ color: entries.length - enteredCount > 0 ? '#a35800' : undefined }} /></Card>
      </div>

      <Card size="small">
        {entries.length === 0 ? (
          <Empty description="No enrolled students in this section." style={{ padding: 20 }} />
        ) : (
          <Table<MarkEntryRow>
            size="small"
            pagination={false}
            rowKey="student_id"
            dataSource={merged}
            columns={[
              { title: 'Adm. no.', dataIndex: 'admission_number', key: 'a', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Name', dataIndex: 'name', key: 'n' },
              {
                title: 'Marks',
                key: 'm',
                width: 130,
                render: (_, r) => (
                  <InputNumber
                    size="small"
                    min={0}
                    max={es.max_marks}
                    value={r.marks_obtained ?? undefined}
                    disabled={r.absent}
                    onChange={(v) => update(r.student_id, { marks_obtained: v ?? null })}
                    style={{ width: 100 }}
                    controls={false}
                  />
                ),
              },
              {
                title: 'Absent',
                key: 'ab',
                width: 80,
                render: (_, r) => (
                  <Switch
                    size="small"
                    checked={r.absent}
                    onChange={(v) => update(r.student_id, { absent: v, marks_obtained: v ? null : r.marks_obtained })}
                  />
                ),
              },
              {
                title: 'Result',
                key: 'res',
                width: 80,
                render: (_, r) => {
                  if (r.absent) return <Tag>abs</Tag>;
                  if (r.marks_obtained == null) return <Tag>—</Tag>;
                  return r.marks_obtained >= es.passing_marks ? <Tag color="success">pass</Tag> : <Tag color="error">fail</Tag>;
                },
              },
              {
                title: 'Moderated',
                key: 'mod',
                width: 100,
                render: (_, r) => r.moderated ? <Tag color="purple">yes</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
              },
            ]}
          />
        )}
      </Card>

      {dirty > 0 && (
        <Popconfirm title={`Save ${dirty} change${dirty === 1 ? '' : 's'}?`} onConfirm={() => save.mutate()} okText="Save">
          <Button type="primary" icon={<SaveOutlined />}>Save</Button>
        </Popconfirm>
      )}
    </Space>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
